import {
  Badge,
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import { useState } from 'react';

interface BulkActionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedMappings: number[];
  onBulkAction: (action: string, mappingIds: number[]) => void;
}

export const BulkActionsModal: React.FC<BulkActionsModalProps> = ({
  isOpen,
  onClose,
  selectedMappings,
  onBulkAction,
}) => {
  const toast = useCustomToast();
  const [selectedAction, setSelectedAction] = useState<string>('');
  const [confirmAction, setConfirmAction] = useState(false);

  const handleBulkAction = () => {
    if (!selectedAction) {
      toast({
        title: 'Action Required',
        description: 'Please select an action to perform on the selected mappings.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    if (!confirmAction) {
      toast({
        title: 'Confirmation Required',
        description: 'Please confirm that you want to perform this bulk action.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Processing Bulk Action...',
      description: `Processing ${selectedAction} for ${selectedMappings.length} mappings.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });

    onBulkAction(selectedAction, selectedMappings);
    onClose();

    // Success toast after action
    setTimeout(() => {
      toast({
        title: 'Bulk Action Completed! ✅',
        description: `Successfully applied "${selectedAction}" to ${selectedMappings.length} mappings.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    }, 2000);
  };

  const actionOptions = [
    { value: '', label: 'Select an action...' },
    { value: 'verify', label: 'Mark as Verified' },
    { value: 'unverify', label: 'Mark as Unverified' },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='lg'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Bulk Actions</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='stretch'>
            <Text fontSize='md' color='gray.600'>
              You have selected <Badge colorScheme='blue'>{selectedMappings.length}</Badge> mappings for bulk action.
            </Text>

            <Select
              placeholder='Choose an action'
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}>
              {actionOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>

            {selectedAction && (
              <Checkbox isChecked={confirmAction} onChange={(e) => setConfirmAction(e.target.checked)}>
                I confirm I want to apply &quot;
                {actionOptions.find((opt) => opt.value === selectedAction)?.label}
                &quot; to {selectedMappings.length} mappings
              </Checkbox>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme='blue' onClick={handleBulkAction} isDisabled={!selectedAction || !confirmAction}>
              Apply Action
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
